import {
  HOME_SIZES, LOCAL_DEFAULTS, DEFAULT_RATE_PER_MOVER_HOUR, DEFAULT_TRUCK_FEE, LONG_HAUL_MILES,
  readAmount, fieldError, localDefault, longDistanceDefault, estimateRange,
} from "./movingEstimate";
import { useMemo, useRef, useState } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { useShareGate } from "@/hooks/useShareGate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TOOL_INFO_CONTENT = {
  what: `Gives a planning-stage estimate of moving costs based on home size, distance, and services needed (like a full packing service), useful for budgeting before collecting real quotes from movers.`,
  how: `Select your home size and move type. Long-distance moves use the distance; local moves are priced by movers, hours and an hourly rate. You get a low to high range. Add and toggle any extra services. Your estimated total cost breaks down by category.`,
  tip: `Treat this as a planning estimate, not a quote — get actual quotes from real moving companies once you're ready to book, since real pricing varies significantly by company, season, and the specifics of your move.`,
};

const clean = (s: string) => readAmount(s) ?? 0;
const BadHint = ({ msg, id }: { msg: string | null; id: string }) => (msg ? <p id={id} className="text-xs text-destructive mt-1" role="alert">{msg}</p> : null);
const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const ResetNote = ({ onReset }: { onReset: () => void }) => (
  <p className="text-xs mt-1 text-muted-foreground">Using your quote · <button type="button" className="underline text-primary" onClick={onReset}>Reset to estimate</button></p>
);

export default function MovingCostEstimator() {
  const [homeSize, setHomeSize] = useState("2 Bedroom");
  const [distance, setDistance] = useState("500");
  const [moveType, setMoveType] = useState<"local" | "long">("long");
  const [moveCostOverride, setMoveCostOverride] = useState<string | null>(null);
  const [packingService, setPackingService] = useState(false);
  const [packingCostOverride, setPackingCostOverride] = useState<string | null>(null);
  const [extraServices, setExtraServices] = useState("0");
  const [crewOverride, setCrewOverride] = useState<string | null>(null);
  const [hoursOverride, setHoursOverride] = useState<string | null>(null);
  const [rate, setRate] = useState(String(DEFAULT_RATE_PER_MOVER_HOUR));
  const [truckFee, setTruckFee] = useState(String(DEFAULT_TRUCK_FEE));
  const resultsRef = useRef<HTMLDivElement>(null);

  const errors = {
    distance: moveType === "long" ? fieldError(distance, "the distance in miles") : null,
    crew: moveType === "local" ? fieldError(crewOverride, "the number of movers") : null,
    hours: moveType === "local" ? fieldError(hoursOverride, "the hours") : null,
    rate: moveType === "local" ? fieldError(rate, "the hourly rate") : null,
    truck: moveType === "local" ? fieldError(truckFee, "the truck fee") : null,
    move: fieldError(moveCostOverride, "the movers cost"),
    packing: packingService ? fieldError(packingCostOverride, "the packing cost") : null,
    extras: fieldError(extraServices, "the extra services cost"),
  };
  const invalid = Object.values(errors).some(Boolean);

  const result = useMemo(() => {
    const base = HOME_SIZES[homeSize] || 4000;
    const dist = clean(distance);
    const ld = LOCAL_DEFAULTS[homeSize] ?? { crew: 3, hours: 5 };
    const crew = crewOverride !== null ? clean(crewOverride) : ld.crew;
    const hours = hoursOverride !== null ? clean(hoursOverride) : ld.hours;

    // Local moves are priced by the hour per mover (crew x hours x rate) plus a truck fee; long-distance
    // scales with home size and distance (see longDistanceDefault).
    const moveCostDefault = moveType === "local"
      ? localDefault(crew, hours, clean(rate), clean(truckFee))
      : longDistanceDefault(base, dist);
    const moveIsQuote = moveCostOverride !== null && moveCostOverride.trim() !== "";
    const moveCost = moveIsQuote ? clean(moveCostOverride!) : moveCostDefault;

    const packingDefault = base * 0.15;
    const packingIsQuote = packingCostOverride !== null && packingCostOverride.trim() !== "";
    const packingCost = packingService ? (packingIsQuote ? clean(packingCostOverride!) : packingDefault) : 0;
    const extras = clean(extraServices);
    const total = moveCost + packingCost + extras;

    // Only the parts we estimated get a range; amounts the person typed (quotes, extras) stay fixed.
    const estimated = (moveIsQuote ? 0 : moveCost) + (packingService && !packingIsQuote ? packingCost : 0);
    const range = estimateRange(estimated, total - estimated);
    return { moveCost, moveCostDefault, packingCost, packingDefault, extras, total, range, hasEstimate: estimated > 0, moveIsQuote, packingIsQuote, dist, crew, hours };
  }, [homeSize, distance, moveType, moveCostOverride, packingService, packingCostOverride, extraServices, crewOverride, hoursOverride, rate, truckFee]);

  const { logUsageNow } = useLogUsageOnAction();
  const { gateShare } = useShareGate();

  // Was useLogToolUsage(packingService, undefined, { ownActions: true }) -- unrelated to whether a
  // real result exists. downloadAsImage (the actual deliverable) is
  // the real gate now.
  const downloadAsImage = async () => {
    if (!resultsRef.current || invalid) return;
    const allowed = await logUsageNow();
    if (!allowed) return;
    try {
      const dataUrl = await toPng(resultsRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      downloadBlob(blob, "moving-cost-estimate.png");
      toast.success("Estimate downloaded as image!");
    } catch (err) {
      console.error("Failed to export estimate as image:", err);
      toast.error("Couldn't export the image right now.");
    }
  };

  const shareEstimate = async () => {
    if (invalid) return;
    const summary = `Moving Cost Estimate\nMovers/Transport: $${result.moveCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}${result.packingCost > 0 ? `\nPacking Service: $${result.packingCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}${result.extras > 0 ? `\nExtra Services: $${result.extras.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}\nTotal: $${result.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}${result.hasEstimate ? ` (estimate range ${usd(result.range.low)} to ${usd(result.range.high)})` : ""}\n\nEstimated with FreeDailyPro's Moving Cost Estimator`;
    await gateShare(async () => {
      if (navigator.share) {
        try {
          await navigator.share({ title: "Moving Cost Estimate", text: summary });
          return true;
        } catch {
          return false; // user cancelled the native share sheet: not an error, and not a use
        }
      }
      try {
        await navigator.clipboard.writeText(summary);
        toast.success("Estimate copied to clipboard!");
        return true;
      } catch {
        toast.error("Couldn't copy. Select the estimate and copy it by hand.");
        return false;
      }
    });
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block text-sm">Home Size</Label>
            <Select value={homeSize} onValueChange={setHomeSize}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(HOME_SIZES).map(s => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Move Type</Label>
            <Select
              value={moveType}
              onValueChange={v => setMoveType(v as "local" | "long")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local (same city/metro)</SelectItem>
                <SelectItem value="long">Long-distance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {moveType === "long" && (
            <div>
              <Label htmlFor="mv-distance" className="mb-1 block text-sm">Distance (miles)</Label>
              <Input id="mv-distance" inputMode="decimal"
                value={distance}
                onChange={e => setDistance(e.target.value)}
                aria-invalid={!!errors.distance}
                aria-describedby={errors.distance ? "mv-distance-err" : undefined}
              />
              <BadHint id="mv-distance-err" msg={errors.distance} />
            </div>
          )}
          {moveType === "local" && (
            <div className="grid grid-cols-2 gap-3">
              {([
                ["mv-crew", "Movers (crew size)", crewOverride ?? String(LOCAL_DEFAULTS[homeSize]?.crew ?? 3), setCrewOverride, errors.crew],
                ["mv-hours", "Hours", hoursOverride ?? String(LOCAL_DEFAULTS[homeSize]?.hours ?? 5), setHoursOverride, errors.hours],
                ["mv-rate", "Rate per mover per hour ($)", rate, setRate, errors.rate],
                ["mv-truck", "Truck / travel fee ($)", truckFee, setTruckFee, errors.truck],
              ] as const).map(([id, label, value, set, err]) => (
                <div key={id}>
                  <Label htmlFor={id} className="mb-1 block text-xs">{label}</Label>
                  <Input id={id} inputMode="decimal" value={value} onChange={e => set(e.target.value)} aria-invalid={!!err} aria-describedby={err ? `${id}-err` : undefined} />
                  <BadHint id={`${id}-err`} msg={err} />
                </div>
              ))}
              <p className="col-span-2 text-xs text-muted-foreground">Local movers usually charge by the hour per mover. Starting values are typical for the home size; use your movers' actual rate.</p>
            </div>
          )}
          <div>
            <Label htmlFor="mv-move" className="mb-1 block text-sm">
              Movers / Transport cost ($) — starts as a rough estimate, edit to match an actual local quote
            </Label>
            <Input id="mv-move" inputMode="decimal"
              value={moveCostOverride ?? result.moveCostDefault.toFixed(0)}
              onChange={e => setMoveCostOverride(e.target.value)}
              aria-invalid={!!errors.move}
              aria-describedby={errors.move ? "mv-move-err" : undefined}
            />
            <BadHint id="mv-move-err" msg={errors.move} />
            {moveCostOverride !== null && <ResetNote onReset={() => setMoveCostOverride(null)} />}
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={packingService}
                onChange={e => setPackingService(e.target.checked)}
              />
              Full packing service
            </label>
            {packingService && (
              <div className="mt-2 pl-6">
                <Label htmlFor="mv-pack" className="mb-1 block text-xs text-muted-foreground">
                  Packing service cost ($) — starts as a rough estimate, edit to match an actual local quote
                </Label>
                <Input id="mv-pack" inputMode="decimal"
                  value={packingCostOverride ?? result.packingDefault.toFixed(0)}
                  onChange={e => setPackingCostOverride(e.target.value)}
                  aria-invalid={!!errors.packing}
                  aria-describedby={errors.packing ? "mv-pack-err" : undefined}
                />
                <BadHint id="mv-pack-err" msg={errors.packing} />
                {packingCostOverride !== null && <ResetNote onReset={() => setPackingCostOverride(null)} />}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="mv-extra" className="mb-1 block text-sm">
              Extra Services ($ — storage, specialty items, etc.)
            </Label>
            <Input id="mv-extra" inputMode="decimal"
              value={extraServices}
              onChange={e => setExtraServices(e.target.value)}
              aria-invalid={!!errors.extras}
              aria-describedby={errors.extras ? "mv-extra-err" : undefined}
            />
            <BadHint id="mv-extra-err" msg={errors.extras} />
          </div>
        </div>

        <div className="space-y-3">
          <div ref={resultsRef} className="space-y-3 bg-background p-1">
          <Card className="p-6 text-center">
            {invalid ? (
              <p className="text-sm text-destructive" role="status">Fix the highlighted field to see your estimate.</p>
            ) : (
              <>
                <p className="text-3xl font-bold text-primary">
                  {result.hasEstimate ? `${usd(result.range.low)} – ${usd(result.range.high)}` : usd(result.total)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {result.hasEstimate ? `Estimated total moving cost (range). Midpoint estimate ${usd(result.total)}` : "Total from the amounts you entered"}
                </p>
                {moveType === "long" && result.dist >= LONG_HAUL_MILES && !result.moveIsQuote && (
                  <p className="text-xs text-muted-foreground mt-2">Cross-country quotes are priced by shipment weight and often come in above this range. Get at least three quotes.</p>
                )}
              </>
            )}
          </Card>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between px-3 py-2 rounded bg-muted/30">
              <span>Movers / Transport</span>
              <span>
                $
                {result.moveCost.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
            {result.packingCost > 0 && (
              <div className="flex justify-between px-3 py-2 rounded bg-muted/30">
                <span>Packing Service</span>
                <span>
                  $
                  {result.packingCost.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            )}
            {result.extras > 0 && (
              <div className="flex justify-between px-3 py-2 rounded bg-muted/30">
                <span>Extra Services</span>
                <span>
                  $
                  {result.extras.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            )}
          </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={downloadAsImage} disabled={invalid}>
              <Download className="h-4 w-4 mr-2" /> Download as Image
            </Button>
            <Button variant="outline" onClick={shareEstimate} disabled={invalid}>
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        This is a rough planning estimate based on typical industry ranges, not
        a quote — actual mover pricing varies significantly by company, season,
        and specifics. Get quotes from real movers for anything final.
      </p>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
